import moment from "moment";
import { useNavigate, useSearchParams } from "react-router-dom";
import { concatParams } from "../helper/pathHelper";
import Masthead from "./common/Masthead";
import {
  scrumExamples,
  pomodoroExamples,
  meetingExamples,
  workshopExamples,
  retroExamples,
  talkExamples,
} from "./examples";
import {
  sportExamples,
  eggExamples,
  cookingExamples,
  doughExamples,
  everydayExamples,
} from "./examplesPersonal";
import { useLanguage } from "../i18n/LanguageContext";
import { localizeExamples } from "../i18n/templatesDe";

// Top-level categories (chosen with ?category=..., so a link can open one
// directly), each with one or more groups of templates. Keys double as
// the i18n keys of their names (examples.*).
export const templateCategories = [
  {
    key: "work",
    groups: [
      { key: "meetings", examples: meetingExamples },
      { key: "workshops", examples: workshopExamples },
      { key: "retros", examples: retroExamples },
      { key: "scrum", examples: scrumExamples },
      {
        key: "talks",
        examples: [...talkExamples, ...pomodoroExamples],
      },
    ],
  },
  {
    key: "sport",
    groups: [{ key: "sportFitness", examples: sportExamples }],
  },
  {
    key: "cooking",
    groups: [
      { key: "eggs", examples: eggExamples },
      { key: "kitchen", examples: cookingExamples },
      { key: "dough", examples: doughExamples },
    ],
  },
  {
    key: "everyday",
    groups: [{ key: "everydayLife", examples: everydayExamples }],
  },
];

export default function Example() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { language, t } = useLanguage();
  const category =
    templateCategories.find((c) => c.key === searchParams.get("category")) ||
    templateCategories[0];

  function openExample(agendaTemplate) {
    const now = moment();
    // Cards keep their optional type and note; the id is made unique here.
    const agenda = agendaTemplate.map((item, index) => ({
      id: `${now.toISOString()}-${index}`,
      ...item,
    }));
    navigate({
      pathname: "/",
      search: concatParams({
        starttime: now.toISOString(),
        agenda: JSON.stringify(agenda),
      }),
    });
    // The board should open at its top, not where this long page was.
    window.scrollTo(0, 0);
  }

  return (
    <div className="content-page">
      <Masthead
        title={t("examples.title")}
        subtitle={t("examples.subtitle")}
      />

      <div
        className="example-variants example-categories"
        role="tablist"
        aria-label={t("examples.categories")}
      >
        {templateCategories.map((option) => (
          <button
            key={option.key}
            type="button"
            role="tab"
            aria-selected={option.key === category.key}
            className={`example-chip${
              option.key === category.key ? " is-selected" : ""
            }`}
            onClick={() =>
              setSearchParams({ category: option.key }, { replace: true })
            }
          >
            {t(`examples.${option.key}`)}
          </button>
        ))}
      </div>

      {category.groups.length > 1 && (
        <nav className="example-jump" aria-label={t("examples.groups")}>
          {category.groups.map((group) => (
            <a
              key={group.key}
              href={`#group-${group.key}`}
              onClick={(event) => {
                // Hash routing owns the URL fragment, so scroll by hand.
                event.preventDefault();
                document
                  .getElementById(`group-${group.key}`)
                  ?.scrollIntoView({ behavior: "smooth" });
              }}
            >
              {t(`examples.${group.key}`)}
            </a>
          ))}
        </nav>
      )}

      {category.groups.map((group) => (
        <div
          className="content-card example-card"
          key={group.key}
          id={`group-${group.key}`}
        >
          <h1 className="example-group-title">
            {t(`examples.${group.key}`)}
          </h1>
          {localizeExamples(group.examples, language).map((example) => (
            <section className="example-group" key={example.event}>
              <h2>{example.event}</h2>
              <p className="example-desc">{example.description}</p>
              <div className="example-variants">
                {example.variants.map((variant) => (
                  <button
                    key={variant.label}
                    type="button"
                    className="example-chip"
                    onClick={() => openExample(variant.agenda)}
                  >
                    {variant.label}
                  </button>
                ))}
              </div>
            </section>
          ))}
        </div>
      ))}
    </div>
  );
}
